import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout/index';
import { alpha, Box, Button, FormLabel, IconButton, Stack, Theme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import { APIResponse } from 'tools/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';

// Constants
const EMPTY_OPTION = { value: '', label: '--' };
const DEFAULT_COLOR = '#FFFFFF';
const KPI_COLUMNS = ['column-1', 'column-2', 'column-3', 'column-4'] as const;

// Types
interface ListItem {
    i: number;
    column: string;
    color: string;
    dataset: number;
    iconUrl: string;
    title: string;
}

export interface FormChartColumnsListProps extends FormControlProps {
    columnsUrl: string;
}

interface RowProps {
    row: ListItem;
    datasetColumns: FormComponentValue[];
    onChange: (row: ListItem) => void;
    onDelete: () => void;
    controlProps: FormChartColumnsListProps;
}

// Utility functions
const parseInitialValue = (value: any, nextIndex: React.MutableRefObject<number>): ListItem[] => {
    if (typeof value !== 'string') {
        return [];
    }

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.map((item) => ({ ...item, i: nextIndex.current++ })) : [];
    } catch (e) {
        const defaultItem: ListItem = {
            column: '',
            iconUrl: '',
            color: DEFAULT_COLOR,
            i: nextIndex.current++,
            dataset: 0,
            title: '',
        };
        return [defaultItem];
    }
};

const createNewItem = (index: number): ListItem => ({
    column: '',
    color: DEFAULT_COLOR,
    iconUrl: '',
    i: index,
    dataset: 0,
    title: '',
});

// Custom hooks
const useDatasets = (columnsUrl: string) => {
    return useQuery<FormComponentValue[], Error>({
        queryKey: ['FormDatasetColumnsList', { source: 'dataset' }, columnsUrl],
        queryFn: async () => {
            const response = await instance.get<APIResponse<FormComponentValue[]>>(columnsUrl, {
                params: { source: 'dataset' },
            });
            return response.data.status === 'OK' ? response.data.data : [];
        },
    });
};

const useDatasetColumns = (columnsUrl: string, datasetId: number) => {
    return useQuery<FormComponentValue[], Error>({
        queryKey: ['FormChartColumnsList', { datasetId, source: 'dataset_column', value_type: 'numeric' }, columnsUrl],
        queryFn: async () => {
            const response = await instance.get<APIResponse<FormComponentValue[]>>(columnsUrl, {
                params: { datasetId, source: 'dataset_column', value_type: 'numeric' },
            });
            return response.data.status === 'OK' ? response.data.data : [];
        },
        enabled: datasetId > 0,
    });
};

// Sub-components
function KPIColumnSelect({
    form,
    columnName,
    label,
    columns,
}: {
    form: any;
    columnName: string;
    label: string;
    columns: FormComponentValue[];
}) {
    const { t } = useBundleTranslation('form');

    return (
        <Box sx={{ width: '100%' }}>
            <FormLabel sx={{ display: 'block' }} className={'design-variant--secondary'}>
                {t(label)}
            </FormLabel>
            <TemplateFormComponent
                componentValues={columns}
                component={'FormSelect'}
                elementProps={form}
                name={columnName}
                designVariant={'secondary'}
            />
        </Box>
    );
}

function Row({ row, onChange, onDelete, datasetColumns, controlProps }: RowProps) {
    const { t } = useBundleTranslation('form');
    const form = useCustomSimplifiedForm(row);
    const watch = form.form.hookFormWatch();

    // Handle form changes
    useEffect(() => {
        if (JSON.stringify(watch) !== JSON.stringify(row)) {
            onChange(watch);
        }
    }, [watch, row, onChange]);

    // Fetch dataset columns
    const { data: allowedColumns, refetch } = useDatasetColumns(controlProps.columnsUrl, row.dataset);
    const columns = useMemo(
        () => [
            EMPTY_OPTION,
            ...(allowedColumns?.map((row) => {
                return { label: row.label, value: row.label };
            }) ?? []),
        ],
        [allowedColumns],
    );

    // Refetch columns when dataset changes
    useEffect(() => {
        if (row.dataset) {
            refetch();
        }
    }, [row.dataset, refetch]);

    return (
        <Stack
            direction="row"
            alignContent="center"
            sx={{ border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}` }}
        >
            <Stack
                direction="column"
                spacing={1}
                width="100%"
                sx={{
                    p: 1,
                    '.select-holder': {
                        overflow: 'visible',
                    },
                }}
            >
                {/* Title */}
                <TemplateFormComponent
                    elementProps={form}
                    component="FormText"
                    name="title"
                    props={{ autoFocus: true }}
                    designVariant={'secondary'}
                />

                {/* Icon URL */}
                <TemplateFormComponent
                    elementProps={form}
                    component="FormText"
                    label="Title Icon URL"
                    name="iconUrl"
                    designVariant={'secondary'}
                />

                {/* Color Picker */}
                <Box>
                    <FormLabel sx={{ display: 'block' }} className={'design-variant--secondary'}>
                        {t('color')}
                    </FormLabel>
                    <TemplateFormComponent
                        elementProps={form}
                        component="ColorPicker"
                        name="color"
                        designVariant={'secondary'}
                    />
                </Box>

                {/* Dataset Select */}
                <Box>
                    <FormLabel sx={{ display: 'block' }} className={'design-variant--secondary'}>
                        {t('dataset')}
                    </FormLabel>
                    <TemplateFormComponent
                        componentValues={datasetColumns}
                        component="FormSelect"
                        elementProps={form}
                        name="dataset"
                        designVariant={'secondary'}
                    />
                </Box>

                {/* KPI Column Selects */}
                {KPI_COLUMNS.map((columnName, index) => (
                    <KPIColumnSelect
                        key={columnName}
                        form={form}
                        columnName={columnName}
                        label={`KPI ${index + 1} Column`}
                        columns={columns}
                    />
                ))}
            </Stack>

            {/* Delete Button */}
            <Box sx={{ p: 1, pl: 0 }}>
                <IconButton sx={{ padding: 0 }} onClick={onDelete}>
                    <IconMi icon="times" fontSize="16" />
                </IconButton>
            </Box>
        </Stack>
    );
}

// Main component
export default function FormKPISectionList({ controlProps }: FormElementControlPropsType<FormChartColumnsListProps>) {
    const { t } = useBundleTranslation('form');
    const nextIndex = useRef(0);
    const [currentValue, setCurrentValue] = useState<ListItem[]>([]);

    // Fetch datasets
    const { data: datasets } = useDatasets(controlProps.columnsUrl);
    const datasetColumns = useMemo(() => [EMPTY_OPTION, ...(datasets ?? [])], [datasets]);

    // Initialize component value
    useEffect(() => {
        const initialValue = parseInitialValue(controlProps.value, nextIndex);
        setCurrentValue(initialValue);
    }, []);

    // Event handlers
    const updateValue = (newValue: ListItem[]) => {
        setCurrentValue(newValue);
        controlProps.onChange(JSON.stringify(newValue));
    };

    const handleRowChange = (index: number, row: ListItem) => {
        const newValue = [...currentValue];
        newValue[index] = { ...newValue[index], ...row };
        updateValue(newValue);
    };

    const handleDelete = (rowIndex: number) => {
        const index = currentValue.findIndex((item) => item.i === rowIndex);
        if (index === -1) return;

        const newValue = [...currentValue];
        newValue.splice(index, 1);
        updateValue(newValue);
    };

    const handleAddItem = () => {
        const newRow = createNewItem(nextIndex.current++);
        updateValue([...currentValue, newRow]);
    };

    return (
        <Box>
            <Stack direction="column" spacing={1} width="100%">
                {currentValue.map((row, index) => (
                    <Row
                        key={row.i}
                        row={row}
                        controlProps={controlProps}
                        datasetColumns={datasetColumns}
                        onChange={(newRow) => handleRowChange(index, newRow)}
                        onDelete={() => handleDelete(row.i)}
                    />
                ))}
                <Box sx={{ width: '150px' }}>
                    <Button onClick={handleAddItem} startIcon={<IconMi icon="new" />} variant="light">
                        {t('add_item')}
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
}

// Builder class
export class FormKPISectionBuilder extends FormComponentBuilder {
    prepareProps(): FormChartColumnsListProps {
        return {
            ...this.controlProps,
            columnsUrl: this.elementProps.componentProps?.columnsUrl ?? '',
        };
    }
}
